import React from 'react';
import { formatUsageMoney, getBillingTermsSummary } from '../config/billing';
import { getCachedBillingProfile } from '../utils/billingRegion';

/**
 * Shared payment / usage terms copy for the Terms page.
 *
 * The per-transaction rate is not hardcoded: when the signed-in store's
 * billing profile is available (it may carry a per-device price override),
 * that rate is shown; otherwise the copy falls back to the standard
 * regional rates.
 */
export function BillingTermsSection({ headingStyle, paragraphStyle }) {
  const profile = getCachedBillingProfile();
  const hasStoreRate =
    profile && typeof profile.pricePerTransactionDollars === 'number' && profile.currency;

  const summary = getBillingTermsSummary(
    profile?.countryCode ? { countryCode: profile.countryCode } : {}
  );

  const storeRateText = hasStoreRate
    ? `${formatUsageMoney(profile.pricePerTransactionDollars, profile.currency)} per completed transaction${profile.regionLabel ? ` (${profile.regionLabel})` : ''}`
    : null;

  return (
    <>
      <h3 style={headingStyle}>5. Transaction Fees</h3>
      <p style={paragraphStyle}>
        In consideration of the Service, you agree to pay RetechSys LLC a fee for each completed
        transaction processed through your provider account (the &quot;Transaction Fee&quot;). The
        Transaction Fee applicable to your store is the rate agreed upon between you and ReTechSys
        at onboarding, as displayed in the Provider Portal and confirmed during payment setup.
        {storeRateText ? (
          <>
            {' '}
            The rate currently applicable to your account is <strong>{storeRateText}</strong>.
          </>
        ) : (
          <>
            {' '}
            Unless a different rate has been agreed in writing, the standard rate is{' '}
            {summary.perTransactionText}.
          </>
        )}
      </p>
      <p style={paragraphStyle}>
        Transaction Fees accrue as transactions are completed and are calculated based on actual
        usage during each calendar month. Fees are billed in Canadian dollars for stores located in
        Canada and in US dollars for stores located in the United States.
      </p>
      <p style={paragraphStyle}>
        {summary.monthlyBillingText} By saving a payment method during onboarding, you authorize
        ReTechSys and Stripe to charge your payment method once per month for the total Transaction
        Fees accrued during that period. Individual transactions are not charged at the time they
        occur.
      </p>
      <p style={paragraphStyle}>
        ReTechSys may revise the Transaction Fee applicable to your store upon reasonable prior
        notice. Continued use of the Service after the effective date of a revised rate constitutes
        acceptance of that rate. Rates agreed in writing for a fixed term remain in effect for the
        duration of that term.
      </p>

      <h3 style={headingStyle}>6. Payment Method</h3>
      <p style={paragraphStyle}>
        You must maintain a valid payment method on file at all times while the Service is active.
        You agree to provide accurate, current, and complete payment information and to promptly
        update it as needed. Amounts invoiced are due upon receipt; failure to pay invoiced amounts
        may result in suspension or termination of your account, and accrued Transaction Fees
        remain payable notwithstanding any suspension or termination.
      </p>
    </>
  );
}
